/**
 * Dungeon interior assembly (O-025) — builds a walkable THREE.Group for a generated
 * DungeonPlan out of the live `dungeon-interior-kit-v1` pieces via the existing
 * AssetLoader (WR.3.2 path — graceful primitive fallback when a piece is absent).
 *
 * Client-local techdemo preview: a schema-v2 plan is assembled from its layout
 * (AD-404 — sized chambers, straight corridors, chokes; `buildLayoutInterior`); a
 * schema-1 plan falls back to the v1 grid for one release. Structural meshes
 * are measured and fitted at runtime, corridors bridge connected rooms, role
 * dressing comes from the theme's template assetIds, and planned encounters render
 * as static blockout proxies. No combat, protocol, or server state.
 *
 * Wall collision IS returned (`BuiltInterior.collision`), derived from the layout by
 * `interiorCollision.ts` rather than from the meshes assembled here — the shared
 * collider is the source of truth, never the render mesh (WR.2 / AD-044).
 */

import * as THREE from 'three';

import type { CollisionShape } from '@great-cloud/shared/client';
import type {
  DungeonCatalog,
  DungeonLayout,
  DungeonLayoutChamber,
  DungeonPlan,
  DungeonPoint,
  DungeonRoom,
  DungeonTheme,
} from '@great-cloud/procedural-dungeons';

import type { AssetLoader } from '../assets/loader.js';
import { fullDetailAssetsActive } from '../scene/assetDetail.js';
import { buildInteriorCollision, buildLayoutCollision } from './interiorCollision.js';
import { DUNGEON_INTERIOR_SCALE, ENEMY_PROXY_HEIGHTS } from './interiorScale.js';
import { planLayoutWalls, yawAlong, type LayoutDoorway } from './layoutWalls.js';
import { createLayoutSecretArea, createSecretArea } from './secretArea.js';
import { moodForTheme, multiplyHex, tintObject } from './themeMood.js';

/** Pack-scoped loader resolver (Game supplies PackLoaderCache.loaderFor over the content store). */
export type LoaderFor = (packId: string) => AssetLoader | null;

const SCALE = DUNGEON_INTERIOR_SCALE;
const ROOM_SIZE = SCALE.roomSize;
const WALL_HEIGHT = SCALE.wallHeight;
const BOUNDS_EPSILON = 0.01;

const CARDINAL_SIDES: readonly CardinalSide[] = [
  { dx: 1, dz: 0, yaw: -Math.PI / 2 },
  { dx: -1, dz: 0, yaw: Math.PI / 2 },
  { dx: 0, dz: 1, yaw: Math.PI },
  { dx: 0, dz: -1, yaw: 0 },
];

const ASSEMBLY_ASSET_IDS = new Set(['floor-tile', 'wall-straight', 'doorway', 'threshold']);

const EXIT_ASSET_IDS = new Set(['portcullis', 'ritual-altar']);
const WALL_MOUNTED_ASSET_IDS = new Set(['room-wall-window', 'wall-shackles', 'wall-torch']);

interface CatalogEnemyProxy {
  id: string;
  label?: string;
  proxy?: { pack: string; model: string };
  boss?: boolean;
}

interface CardinalSide {
  dx: number;
  dz: number;
  yaw: number;
}

interface MeasuredPiece {
  root: THREE.Object3D;
  size: THREE.Vector3;
}

interface OccupiedDressingSpace {
  x: number;
  z: number;
  radius: number;
}

function fallbackBox(width: number, height: number, depth: number, colour: number): THREE.Mesh {
  const geometry = new THREE.BoxGeometry(width, height, depth);
  // Presentation objects use y=0 as their floor contact rather than their centre.
  geometry.translate(0, height / 2, 0);
  const mesh = new THREE.Mesh(
    geometry,
    new THREE.MeshStandardMaterial({ color: colour, roughness: 0.9 }),
  );
  mesh.castShadow = mesh.receiveShadow = true;
  return mesh;
}

function fallbackDoorway(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'fallback:doorway-frame';

  const jambOffset = SCALE.doorwayClearWidth / 2 + SCALE.doorwayJambWidth / 2;
  const leftJamb = fallbackBox(
    SCALE.doorwayJambWidth,
    SCALE.doorwayClearHeight,
    SCALE.wallThickness,
    0x5b5148,
  );
  leftJamb.position.x = -jambOffset;
  group.add(leftJamb);

  const rightJamb = leftJamb.clone();
  rightJamb.position.x = jambOffset;
  group.add(rightJamb);

  const lintelHeight = WALL_HEIGHT - SCALE.doorwayClearHeight;
  const lintel = fallbackBox(
    SCALE.doorwayFrameOuterWidth,
    lintelHeight,
    SCALE.wallThickness,
    0x5b5148,
  );
  lintel.position.y = SCALE.doorwayClearHeight;
  group.add(lintel);

  return group;
}

function enableShadows(object: THREE.Object3D): void {
  object.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
  });
}

async function place(
  loader: AssetLoader | null,
  assetId: string,
  fallback: () => THREE.Object3D,
): Promise<THREE.Object3D> {
  const instance = loader
    ? await loader.instantiatePreferred(assetId, fullDetailAssetsActive()).catch(() => null)
    : null;
  return instance ?? fallback();
}

/** Rebase an arbitrary authored object to a centred x/z footprint with its base at y=0. */
function groundAndMeasure(object: THREE.Object3D): MeasuredPiece | null {
  object.updateMatrixWorld(true);
  const bounds = new THREE.Box3().setFromObject(object);
  if (bounds.isEmpty()) return null;

  const rawSize = bounds.getSize(new THREE.Vector3());
  const values = [
    bounds.min.x,
    bounds.min.y,
    bounds.min.z,
    bounds.max.x,
    bounds.max.y,
    bounds.max.z,
    rawSize.x,
    rawSize.y,
    rawSize.z,
  ];
  if (values.some((value) => !Number.isFinite(value))) return null;
  if (Math.max(rawSize.x, rawSize.y, rawSize.z) <= BOUNDS_EPSILON) return null;

  const centre = bounds.getCenter(new THREE.Vector3());
  object.position.x -= centre.x;
  object.position.y -= bounds.min.y;
  object.position.z -= centre.z;

  const root = new THREE.Group();
  root.add(object);
  enableShadows(root);

  return {
    root,
    size: new THREE.Vector3(
      Math.max(rawSize.x, BOUNDS_EPSILON),
      Math.max(rawSize.y, BOUNDS_EPSILON),
      Math.max(rawSize.z, BOUNDS_EPSILON),
    ),
  };
}

async function loadMeasured(
  loader: AssetLoader | null,
  assetId: string,
  fallback: () => THREE.Object3D,
): Promise<MeasuredPiece> {
  const candidate = await place(loader, assetId, fallback);
  const measured = groundAndMeasure(candidate);
  if (measured) return measured;

  const fallbackMeasured = groundAndMeasure(fallback());
  if (!fallbackMeasured) {
    throw new Error(`Dungeon fallback for ${assetId} has no measurable geometry`);
  }
  return fallbackMeasured;
}

function fittedClone(
  piece: MeasuredPiece,
  width: number,
  height: number,
  depth: number,
): THREE.Object3D {
  const clone = piece.root.clone(true);
  clone.scale.set(width / piece.size.x, height / piece.size.y, depth / piece.size.z);
  return clone;
}

/** Tile repeated measured wall pieces across an exact local-x interval. */
function tileWallSpan(
  parent: THREE.Object3D,
  wallPiece: MeasuredPiece,
  span: number,
  startX: number,
  namePrefix: string,
  height: number = WALL_HEIGHT,
): void {
  if (span <= BOUNDS_EPSILON) return;

  const nominalTileSpan = THREE.MathUtils.clamp(
    wallPiece.size.x,
    SCALE.minimumWallTileSpan,
    SCALE.maximumWallTileSpan,
  );
  const tileCount = Math.max(1, Math.ceil(span / nominalTileSpan));
  const exactTileSpan = span / tileCount;

  for (let index = 0; index < tileCount; index += 1) {
    const tile = fittedClone(wallPiece, exactTileSpan, height, SCALE.wallThickness);
    tile.name = `${namePrefix}:tile-${index}`;
    tile.position.x = startX + exactTileSpan * (index + 0.5);
    parent.add(tile);
  }
}

function sameSide(left: CardinalSide, right: CardinalSide): boolean {
  return left.dx === right.dx && left.dz === right.dz;
}

function buildDressingCandidates(): readonly THREE.Vector2[] {
  const candidates: THREE.Vector2[] = [];
  const outerRadius = ROOM_SIZE / 2 - SCALE.wallThickness - SCALE.dressingWallClearance;

  for (let ring = 0; ring < SCALE.dressingCandidateRingCount; ring += 1) {
    const radius = outerRadius - ring * SCALE.dressingRingSpacing;
    if (radius <= 0) break;
    const phase = ring % 2 === 0 ? 0 : Math.PI / SCALE.dressingCandidatesPerRing;
    for (let slot = 0; slot < SCALE.dressingCandidatesPerRing; slot += 1) {
      const angle = ((slot + 0.5) / SCALE.dressingCandidatesPerRing) * Math.PI * 2 + phase;
      candidates.push(new THREE.Vector2(Math.cos(angle) * radius, Math.sin(angle) * radius));
    }
  }

  return candidates;
}

const DRESSING_CANDIDATES = buildDressingCandidates();

function blocksDoorPath(
  x: number,
  z: number,
  radius: number,
  openSides: readonly CardinalSide[],
): boolean {
  const halfKeepout = SCALE.doorwayClearWidth / 2 + SCALE.dressingSpacing + radius;
  return openSides.some((side) => {
    if (side.dx !== 0) {
      return side.dx * x > 0 && Math.abs(z) < halfKeepout;
    }
    return side.dz * z > 0 && Math.abs(x) < halfKeepout;
  });
}

function findDressingPlacement(
  radius: number,
  openSides: readonly CardinalSide[],
  occupied: readonly OccupiedDressingSpace[],
  coreRadius: number,
  startIndex: number,
): THREE.Vector2 | null {
  const edge = ROOM_SIZE / 2 - SCALE.wallThickness - SCALE.dressingWallClearance;

  for (let attempt = 0; attempt < DRESSING_CANDIDATES.length; attempt += 1) {
    const candidate = DRESSING_CANDIDATES[(attempt + startIndex) % DRESSING_CANDIDATES.length]!;
    const x = candidate.x;
    const z = candidate.y;

    if (Math.abs(x) + radius > edge || Math.abs(z) + radius > edge) continue;
    if (Math.hypot(x, z) - radius < coreRadius) continue;
    if (blocksDoorPath(x, z, radius, openSides)) continue;
    if (
      occupied.some(
        (entry) =>
          Math.hypot(x - entry.x, z - entry.z) < radius + entry.radius + SCALE.dressingSpacing,
      )
    ) {
      continue;
    }
    return candidate.clone();
  }

  return null;
}

function knownProxyHeight(proxyId: string): number {
  return (
    ENEMY_PROXY_HEIGHTS[proxyId as keyof typeof ENEMY_PROXY_HEIGHTS] ??
    ENEMY_PROXY_HEIGHTS['skirmisher-proxy']
  );
}

/** Share of a chamber's inscribed radius the encounter bodies scatter over (v1: 0.3 × room = 0.6 × half-room). */
const LAYOUT_ENCOUNTER_DISC = 0.6;
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

/** A flat convex slab (floor or ceiling) for a part that is not an axis-aligned rectangle. */
function polygonSlab(
  points: readonly DungeonPoint[],
  thickness: number,
  colour: number,
): THREE.Mesh {
  const shape = new THREE.Shape(points.map((point) => new THREE.Vector2(point.x, point.z)));
  const geometry = new THREE.ExtrudeGeometry(shape, { depth: thickness, bevelEnabled: false });
  // Shape (x, y) → world (x, z); the extrusion runs down from y = 0 to y = −thickness.
  geometry.rotateX(Math.PI / 2);
  const mesh = new THREE.Mesh(
    geometry,
    new THREE.MeshStandardMaterial({ color: colour, roughness: 0.9, side: THREE.DoubleSide }),
  );
  mesh.castShadow = mesh.receiveShadow = true;
  return mesh;
}

function axisAlignedRect(
  points: readonly DungeonPoint[],
): { minX: number; maxX: number; minZ: number; maxZ: number } | null {
  if (points.length !== 4) return null;
  for (let index = 0; index < 4; index += 1) {
    const a = points[index]!;
    const b = points[(index + 1) % 4]!;
    if (a.x !== b.x && a.z !== b.z) return null;
  }
  const xs = points.map((point) => point.x);
  const zs = points.map((point) => point.z);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minZ: Math.min(...zs),
    maxZ: Math.max(...zs),
  };
}

/** Even-odd point-in-polygon; the outline may be concave (`l`/`t`). */
function insideOutline(x: number, z: number, outline: readonly DungeonPoint[]): boolean {
  let inside = false;
  for (let i = 0, j = outline.length - 1; i < outline.length; j = i, i += 1) {
    const a = outline[i]!;
    const b = outline[j]!;
    if (a.z > z !== b.z > z && x < ((b.x - a.x) * (z - a.z)) / (b.z - a.z) + a.x) inside = !inside;
  }
  return inside;
}

function distanceToOutline(x: number, z: number, outline: readonly DungeonPoint[]): number {
  let best = Infinity;
  for (let index = 0; index < outline.length; index += 1) {
    const a = outline[index]!;
    const b = outline[(index + 1) % outline.length]!;
    const abX = b.x - a.x;
    const abZ = b.z - a.z;
    const lengthSq = abX * abX + abZ * abZ;
    const t =
      lengthSq === 0 ? 0 : Math.max(0, Math.min(1, ((x - a.x) * abX + (z - a.z) * abZ) / lengthSq));
    best = Math.min(best, Math.hypot(x - (a.x + abX * t), z - (a.z + abZ * t)));
  }
  return best;
}

/** How many candidate rings a chamber's dressing lattice may use; a boss hall stops here. */
const LAYOUT_DRESSING_MAX_RINGS = 8;

/**
 * Per-chamber dressing slots (AD-404 §7): polar rings about the chamber centre,
 * outermost first, kept only where the prop clears the outline's walls — the inset
 * polygon, sampled — the encounter core, and a full-width lane from every doorway
 * to the centre.
 */
function findLayoutDressingPlacement(
  chamber: DungeonLayoutChamber,
  doorways: readonly LayoutDoorway[],
  radius: number,
  occupied: readonly OccupiedDressingSpace[],
  coreRadius: number,
  startIndex: number,
): THREE.Vector2 | null {
  const { centre, outline } = chamber;
  const outer = Math.max(
    ...outline.map((point) => Math.hypot(point.x - centre.x, point.z - centre.z)),
  );
  const candidates: THREE.Vector2[] = [];
  for (let ring = 0; ring < LAYOUT_DRESSING_MAX_RINGS; ring += 1) {
    const ringRadius = outer - ring * SCALE.dressingRingSpacing;
    if (ringRadius <= 0) break;
    const phase = ring % 2 === 0 ? 0 : Math.PI / SCALE.dressingCandidatesPerRing;
    for (let slot = 0; slot < SCALE.dressingCandidatesPerRing; slot += 1) {
      const angle = ((slot + 0.5) / SCALE.dressingCandidatesPerRing) * Math.PI * 2 + phase;
      candidates.push(
        new THREE.Vector2(Math.cos(angle) * ringRadius, Math.sin(angle) * ringRadius),
      );
    }
  }
  const wallKeepout = SCALE.wallThickness / 2 + SCALE.dressingWallClearance + radius;
  const halfLane = SCALE.doorwayClearWidth / 2 + SCALE.dressingSpacing + radius;
  const lanes = doorways.map((doorway) => {
    let inX = doorway.along.z;
    let inZ = -doorway.along.x;
    const toCentre = (centre.x - doorway.at.x) * inX + (centre.z - doorway.at.z) * inZ;
    if (toCentre < 0) {
      inX = -inX;
      inZ = -inZ;
    }
    return { doorway, inX, inZ, reach: Math.abs(toCentre) + radius };
  });

  for (let attempt = 0; attempt < candidates.length; attempt += 1) {
    const candidate = candidates[(attempt + startIndex) % candidates.length]!;
    const x = centre.x + candidate.x;
    const z = centre.z + candidate.y;
    if (!insideOutline(x, z, outline)) continue;
    if (distanceToOutline(x, z, outline) < wallKeepout) continue;
    if (candidate.length() - radius < coreRadius) continue;
    const inLane = lanes.some(({ doorway, inX, inZ, reach }) => {
      const relX = x - doorway.at.x;
      const relZ = z - doorway.at.z;
      const depth = relX * inX + relZ * inZ;
      const lateral = Math.abs(relX * doorway.along.x + relZ * doorway.along.z);
      return depth > -radius && depth < reach && lateral < halfLane;
    });
    if (inLane) continue;
    if (
      occupied.some(
        (entry) =>
          Math.hypot(candidate.x - entry.x, candidate.y - entry.z) <
          radius + entry.radius + SCALE.dressingSpacing,
      )
    ) {
      continue;
    }
    return candidate.clone();
  }
  return null;
}

/**
 * Schema-v2 assembly (AD-404 §7, S3): every chamber, corridor and doorway comes
 * from `plan.layout`; walls are tiled along `planLayoutWalls` spans — the walk the
 * collider also reads — so a mesh and its footprint share one source.
 */
async function buildLayoutInterior(
  plan: DungeonPlan,
  layout: DungeonLayout,
  theme: DungeonTheme,
  catalog: DungeonCatalog,
  loaderFor: LoaderFor,
  origin: THREE.Vector3,
  options: InteriorBuildOptions,
): Promise<BuiltInterior> {
  const interiorPackId = theme.roomTemplates[0]?.assetPackId ?? 'dungeon-interior-kit-v1';
  const loader = loaderFor(interiorPackId);
  const group = new THREE.Group();
  group.name = `dungeon:${plan.id}`;
  group.position.copy(origin);
  const mood = moodForTheme(theme.id);
  group.add(new THREE.AmbientLight(mood.ambient.colour, mood.ambient.intensity));

  const layoutInput = { connections: plan.connections, layout };
  const wallPlan = planLayoutWalls(layoutInput, { doorwayJamb: SCALE.doorwayJambWidth });
  const secretArea = createLayoutSecretArea(plan.id, wallPlan.secret, loader);
  const enemyById = new Map<string, CatalogEnemyProxy>(
    (catalog.enemies as unknown as CatalogEnemyProxy[]).map((enemy) => [enemy.id, enemy]),
  );
  const chamberById = new Map(layout.chambers.map((chamber) => [chamber.roomId, chamber]));

  const floorPiece = await loadMeasured(loader, 'floor-tile', () =>
    fallbackBox(
      SCALE.maximumWallTileSpan,
      SCALE.floorThickness,
      SCALE.maximumWallTileSpan,
      0x4a4f55,
    ),
  );
  const wallPiece = await loadMeasured(loader, 'wall-straight', () =>
    fallbackBox(SCALE.maximumWallTileSpan, WALL_HEIGHT, SCALE.wallThickness, 0x565b60),
  );
  const doorwayPiece = await loadMeasured(loader, 'doorway', fallbackDoorway);
  const thresholdPiece = await loadMeasured(loader, 'threshold', () =>
    fallbackBox(SCALE.doorwayClearWidth, SCALE.thresholdHeight, SCALE.wallThickness * 2, 0x62594d),
  );
  // The theme's mood (themeMood.ts). The ceiling is the floor tile turned over, so it
  // takes its own copy before the floor darkens and reads as the walls' stone.
  const ceilingPiece: MeasuredPiece = { root: floorPiece.root.clone(true), size: floorPiece.size };
  tintObject(floorPiece.root, mood.floorTint);
  tintObject(ceilingPiece.root, mood.wallTint);
  tintObject(wallPiece.root, mood.wallTint);
  tintObject(doorwayPiece.root, mood.wallTint);
  tintObject(thresholdPiece.root, mood.floorTint);

  // Floors sit under y = 0; a ceiling is the floor tile turned over so its authored
  // surface faces into the chamber, its base plane on the chamber's wall height —
  // the v1 convention, per convex part instead of per grid square.
  const addSlab = (points: readonly DungeonPoint[], name: string, ceilingAt: number | null) => {
    const thickness = ceilingAt === null ? SCALE.floorThickness : SCALE.ceilingThickness;
    const rect = axisAlignedRect(points);
    if (rect) {
      const piece = ceilingAt === null ? floorPiece : ceilingPiece;
      const slab = fittedClone(piece, rect.maxX - rect.minX, thickness, rect.maxZ - rect.minZ);
      slab.name = name;
      slab.position.set(
        (rect.minX + rect.maxX) / 2,
        ceilingAt ?? -thickness,
        (rect.minZ + rect.maxZ) / 2,
      );
      if (ceilingAt !== null) slab.rotation.x = Math.PI;
      group.add(slab);
      return;
    }
    const slab = polygonSlab(
      points,
      thickness,
      multiplyHex(0x4a4f55, ceilingAt === null ? mood.floorTint : mood.wallTint),
    );
    slab.name = name;
    slab.position.y = ceilingAt === null ? 0 : ceilingAt + thickness;
    group.add(slab);
  };

  for (const chamber of layout.chambers) {
    chamber.convex.forEach((part, index) => {
      addSlab(part, `room:${chamber.roomId}:floor:${index}`, null);
      if (options.ceilings !== false) {
        addSlab(part, `room:${chamber.roomId}:ceiling:${index}`, chamber.wallHeight);
      }
    });
  }

  for (const wall of wallPlan.walls) {
    const length = Math.hypot(wall.to.x - wall.from.x, wall.to.z - wall.from.z);
    const along = { x: (wall.to.x - wall.from.x) / length, z: (wall.to.z - wall.from.z) / length };
    const span = new THREE.Group();
    span.name = wall.name;
    span.position.set((wall.from.x + wall.to.x) / 2, 0, (wall.from.z + wall.to.z) / 2);
    span.rotation.y = yawAlong(along);
    tileWallSpan(span, wallPiece, length, -length / 2, wall.name, wall.height);
    group.add(span);
  }

  for (const doorway of wallPlan.doorways) {
    const frame = new THREE.Group();
    frame.name = doorway.name;
    frame.position.set(doorway.at.x, 0, doorway.at.z);
    frame.rotation.y = yawAlong(doorway.along);
    const outerWidth = doorway.clearWidth + SCALE.doorwayJambWidth * 2;
    const mesh = fittedClone(doorwayPiece, outerWidth, doorway.height, SCALE.wallThickness);
    mesh.name = `${doorway.name}:frame`;
    frame.add(mesh);
    const threshold = fittedClone(
      thresholdPiece,
      doorway.clearWidth,
      SCALE.thresholdHeight,
      SCALE.wallThickness * 2,
    );
    threshold.name = `${doorway.name}:threshold`;
    frame.add(threshold);
    group.add(frame);
  }

  for (const piece of wallPlan.corridors) {
    const length = Math.hypot(piece.to.x - piece.from.x, piece.to.z - piece.from.z);
    const along = {
      x: (piece.to.x - piece.from.x) / length,
      z: (piece.to.z - piece.from.z) / length,
    };
    const run = new THREE.Group();
    run.name = piece.name;
    run.position.set((piece.from.x + piece.to.x) / 2, 0, (piece.from.z + piece.to.z) / 2);
    run.rotation.y = yawAlong(along);
    const width = piece.clearWidth + SCALE.wallThickness * 2;
    const floor = fittedClone(floorPiece, length, SCALE.floorThickness, width);
    floor.name = `${piece.name}:floor`;
    floor.position.y = -SCALE.floorThickness;
    run.add(floor);
    if (options.ceilings !== false) {
      const ceiling = fittedClone(ceilingPiece, length, SCALE.ceilingThickness, width);
      ceiling.name = `${piece.name}:ceiling`;
      ceiling.position.y = piece.height;
      ceiling.rotation.x = Math.PI;
      run.add(ceiling);
    }
    group.add(run);
  }

  for (const room of plan.rooms) {
    const chamber = chamberById.get(room.id);
    if (!chamber) continue;
    const centre = chamber.centre;
    const radius = chamber.inscribedRadius;
    const doorways = wallPlan.doorways.filter((doorway) => doorway.roomId === room.id);
    const occupiedDressing: OccupiedDressingSpace[] = [];
    const template = theme.roomTemplates.find((entry) => entry.id === room.templateId);
    const templateLoader = loaderFor(template?.assetPackId ?? interiorPackId);
    const dressing = (template?.assetIds ?? []).filter(
      (assetId) =>
        !ASSEMBLY_ASSET_IDS.has(assetId) && !(room.role === 'exit' && EXIT_ASSET_IDS.has(assetId)),
    );
    // The encounter core is the disc the §4.1 area was sized to hold the bodies in.
    const coreRadius = room.encounter
      ? radius * LAYOUT_ENCOUNTER_DISC
      : Math.min(SCALE.navigationCoreRadius, radius * 0.5);

    for (const [index, assetId] of dressing.entries()) {
      const propPiece = await loadMeasured(templateLoader, assetId, () =>
        fallbackBox(0.8, 1.1, 0.8, 0x6b5f4d),
      );
      const naturalFootprint = Math.max(propPiece.size.x, propPiece.size.z);
      let propScale = Math.min(1, SCALE.maximumDressingFootprint / naturalFootprint);
      let propRadius = Math.max(0.08, (naturalFootprint * propScale) / 2);
      let placement: THREE.Vector2 | null = null;
      for (let fitAttempt = 0; fitAttempt < 18 && !placement; fitAttempt += 1) {
        propRadius = Math.max(0.08, (naturalFootprint * propScale) / 2);
        placement = findLayoutDressingPlacement(
          chamber,
          doorways,
          propRadius,
          occupiedDressing,
          coreRadius,
          index * 11,
        );
        if (!placement) propScale *= 0.82;
      }
      if (!placement) continue;

      const prop = propPiece.root.clone(true);
      prop.name = `room:${room.id}:dressing:${assetId}:${index}`;
      prop.scale.setScalar(propScale);
      const isWallMounted = WALL_MOUNTED_ASSET_IDS.has(assetId);
      const mountedHeight = Math.max(
        0,
        Math.min(
          SCALE.wallMountBaseHeight,
          chamber.wallHeight - propPiece.size.y * propScale - SCALE.ceilingThickness - 0.1,
        ),
      );
      prop.position.set(
        centre.x + placement.x,
        isWallMounted ? mountedHeight : 0,
        centre.z + placement.y,
      );
      prop.rotation.y = Math.atan2(-placement.x, -placement.y);
      group.add(prop);
      occupiedDressing.push({ x: placement.x, z: placement.y, radius: propRadius });
    }

    if (room.encounter) {
      const isBossRoom = room.encounter.kind === 'boss';
      const disc = radius * LAYOUT_ENCOUNTER_DISC;
      if (isBossRoom) {
        const ring = new THREE.Mesh(
          new THREE.RingGeometry(disc * 0.92, disc, 48),
          new THREE.MeshBasicMaterial({
            color: 0xe8443a,
            transparent: true,
            opacity: 0.35,
            side: THREE.DoubleSide,
          }),
        );
        ring.name = `room:${room.id}:boss-ring`;
        ring.rotation.x = -Math.PI / 2;
        ring.position.set(centre.x, 0.04, centre.z);
        group.add(ring);
      }

      // A networked run's enemies are the server's entities (AD-705): no stand-ins.
      const plannedMembers =
        options.encounterProxies === false
          ? []
          : room.encounter.members.flatMap((member) =>
              Array.from({ length: member.count }, () => member),
            );
      // Sunflower scatter over the inscribed disc: even density, deterministic, and
      // the bodies land in the area §4.1 sized for them — the visual check of it.
      const scatter = plannedMembers.length <= 1 ? 0 : disc * 0.85;
      for (const [slot, member] of plannedMembers.entries()) {
        const enemy = enemyById.get(member.enemyId);
        const memberIsKnownProxy = Object.prototype.hasOwnProperty.call(
          ENEMY_PROXY_HEIGHTS,
          member.enemyId,
        );
        const proxyId =
          enemy?.proxy?.model ?? (memberIsKnownProxy ? member.enemyId : 'skirmisher-proxy');
        const proxyLoader = enemy?.proxy?.pack ? loaderFor(enemy.proxy.pack) : null;
        const proxyHeight = knownProxyHeight(proxyId);
        const proxyWidth = Math.max(0.4, proxyHeight * 0.32);
        const proxyPiece = await loadMeasured(proxyLoader, proxyId, () =>
          fallbackBox(proxyWidth, proxyHeight, proxyWidth, isBossRoom ? 0x8a3a34 : 0x777d84),
        );
        const proxy = proxyPiece.root.clone(true);
        proxy.name = `room:${room.id}:encounter:${proxyId}:${slot}`;
        const r = scatter * Math.sqrt((slot + 0.5) / plannedMembers.length);
        const angle = slot * GOLDEN_ANGLE;
        const localX = Math.cos(angle) * r;
        const localZ = Math.sin(angle) * r;
        proxy.position.set(centre.x + localX, 0, centre.z + localZ);
        proxy.rotation.y = Math.atan2(-localX, -localZ);
        group.add(proxy);
      }
    }

    if (room.role === 'exit') {
      // Visual exit dressing on the longest doorway-free wall that can hold it — a
      // portcullis in a walkable doorway would contradict the collision (§12c).
      const exitWall = wallPlan.walls
        .filter((wall) => wall.name.startsWith(`room:${room.id}:wall:`))
        .map((wall) => ({
          wall,
          length: Math.hypot(wall.to.x - wall.from.x, wall.to.z - wall.from.z),
        }))
        .filter((entry) => entry.length >= SCALE.doorwayFrameOuterWidth)
        .sort((left, right) => right.length - left.length)[0];
      if (exitWall) {
        const { wall, length } = exitWall;
        const along = {
          x: (wall.to.x - wall.from.x) / length,
          z: (wall.to.z - wall.from.z) / length,
        };
        const mid = { x: (wall.from.x + wall.to.x) / 2, z: (wall.from.z + wall.to.z) / 2 };
        let inX = along.z;
        let inZ = -along.x;
        if ((centre.x - mid.x) * inX + (centre.z - mid.z) * inZ < 0) {
          inX = -inX;
          inZ = -inZ;
        }
        const gatePiece = await loadMeasured(templateLoader, 'portcullis', () =>
          fallbackBox(
            SCALE.doorwayFrameOuterWidth,
            SCALE.doorwayClearHeight,
            SCALE.wallThickness,
            0x3c3f45,
          ),
        );
        const gate = fittedClone(
          gatePiece,
          SCALE.doorwayFrameOuterWidth,
          Math.min(SCALE.doorwayClearHeight, chamber.wallHeight),
          SCALE.wallThickness,
        );
        gate.name = `room:${room.id}:exit-portcullis`;
        const wallInset = SCALE.wallThickness * 0.6;
        gate.position.set(mid.x + inX * wallInset, 0, mid.z + inZ * wallInset);
        gate.rotation.y = yawAlong(along);
        group.add(gate);
      }

      const altarPiece = await loadMeasured(templateLoader, 'ritual-altar', () =>
        fallbackBox(1.4, 1, 1.4, 0x7a6a3f),
      );
      const altarNaturalFootprint = Math.max(altarPiece.size.x, altarPiece.size.z);
      const altarScale = Math.min(1, SCALE.maximumDressingFootprint / altarNaturalFootprint);
      const altarRadius = Math.max(0.08, (altarNaturalFootprint * altarScale) / 2);
      const altarPlacement = findLayoutDressingPlacement(
        chamber,
        doorways,
        altarRadius,
        occupiedDressing,
        coreRadius,
        dressing.length * 13 + 5,
      );
      if (altarPlacement) {
        const altar = altarPiece.root.clone(true);
        altar.name = `room:${room.id}:exit-altar`;
        altar.scale.setScalar(altarScale);
        altar.position.set(centre.x + altarPlacement.x, 0, centre.z + altarPlacement.y);
        altar.rotation.y = Math.atan2(-altarPlacement.x, -altarPlacement.y);
        group.add(altar);
      }
    }

    const isBoss = room.encounter?.kind === 'boss';
    const light = isBoss ? mood.boss : mood.torch;
    const torch = new THREE.PointLight(
      light.colour,
      light.intensity,
      Math.max(12, radius * 3),
      mood.torchDecay,
    );
    torch.name = `room:${room.id}:light`;
    torch.position.set(centre.x, chamber.wallHeight * 0.75, centre.z);
    group.add(torch);
  }

  const secretGroup = await secretArea.build();
  // The alcove builds its own pieces; it takes the same mood, or its false-wall
  // panel would stand out against the tinted walls and give the secret away.
  const secretStone: THREE.Object3D[] = [];
  secretGroup.traverse((child) => {
    if (
      /:(floor|threshold|ceiling|side-wall:-?\d+|back-wall|entrance-jamb:-?\d+|false-wall-panel)$/.test(
        child.name,
      )
    ) {
      secretStone.push(child);
    }
  });
  for (const piece of secretStone) {
    tintObject(piece, /:(floor|threshold)$/.test(piece.name) ? mood.floorTint : mood.wallTint);
  }
  if (options.ceilings === false) {
    // The alcove roofs itself (secretArea.ts); drop it like the chamber roofs. Never
    // uploaded, so nothing to dispose — and its geometry may be the template's.
    const roofs: THREE.Object3D[] = [];
    secretGroup.traverse((child) => {
      if (child.name.endsWith(':ceiling')) roofs.push(child);
    });
    for (const roof of roofs) roof.removeFromParent();
  }
  group.add(secretGroup);

  const margin = SCALE.wallThickness;
  const minX = Math.min(layout.bounds.minX - margin, secretArea.bounds.minX);
  const maxX = Math.max(layout.bounds.maxX + margin, secretArea.bounds.maxX);
  const minZ = Math.min(layout.bounds.minZ - margin, secretArea.bounds.minZ);
  const maxZ = Math.max(layout.bounds.maxZ + margin, secretArea.bounds.maxZ);

  const centreOf = (room: DungeonRoom): THREE.Vector3 => {
    const chamber = chamberById.get(room.id);
    return chamber ? new THREE.Vector3(chamber.centre.x, 0, chamber.centre.z) : new THREE.Vector3();
  };
  const entranceRoom = plan.rooms.find((room) => room.role === 'entrance') ?? plan.rooms[0]!;
  return {
    group,
    entrance: centreOf(entranceRoom).add(origin),
    roomCentre: (room) => centreOf(room).add(origin),
    bounds: {
      minX: origin.x + minX,
      maxX: origin.x + maxX,
      minZ: origin.z + minZ,
      maxZ: origin.z + maxZ,
    },
    collision: buildLayoutCollision(layoutInput),
  };
}

export interface BuiltInterior {
  group: THREE.Group;
  entrance: THREE.Vector3;
  roomCentre: (room: DungeonRoom) => THREE.Vector3;
  bounds: { minX: number; maxX: number; minZ: number; maxZ: number };
  /**
   * Static wall collision in the group's LOCAL frame, derived from the layout by
   * `interiorCollision.ts` — never from the meshes below. A walker resolves against
   * this with the shared `resolveCircle`; `bounds` stays what it always was (a
   * presentation extent), not a substitute for it.
   */
  collision: CollisionShape[];
}

export interface InteriorBuildOptions {
  /**
   * Stand static enemy proxies in each encounter room (default true — the
   * client-local techdemo explorer). A networked run passes false: its enemies
   * are the server's entities, drawn by the snapshot stream (AD-705).
   */
  encounterProxies?: boolean;
  /**
   * Roof the chambers and corridors (default true — the explorer's boom is pulled
   * in under them). A networked run passes false: the game's orbit camera rides
   * above wall height and would otherwise look down on the roof (AD-705 lane run).
   */
  ceilings?: boolean;
}

export async function buildDungeonInterior(
  plan: DungeonPlan,
  theme: DungeonTheme,
  catalog: DungeonCatalog,
  loaderFor: LoaderFor,
  origin: THREE.Vector3,
  options: InteriorBuildOptions = {},
): Promise<BuiltInterior> {
  if (plan.layout) {
    return buildLayoutInterior(plan, plan.layout, theme, catalog, loaderFor, origin, options);
  }
  // v1 fallback (schema-1 plan, no layout) — kept for exactly one release (AD-404 §8).
  const interiorPackId = theme.roomTemplates[0]?.assetPackId ?? 'dungeon-interior-kit-v1';
  const loader = loaderFor(interiorPackId);
  const group = new THREE.Group();
  group.name = `dungeon:${plan.id}`;
  group.position.copy(origin);

  // Presentation spacing may expand authored grid spacing, but never contracts it.
  // This prevents the newly scaled rooms from overlapping and guarantees a useful
  // corridor run between their wall lines.
  const spacing = Math.max(theme.roomSpacing ?? 0, ROOM_SIZE + SCALE.minimumCorridorLength);
  const secretArea = createSecretArea(plan, loader, spacing);
  const enemyById = new Map<string, CatalogEnemyProxy>(
    (catalog.enemies as unknown as CatalogEnemyProxy[]).map((enemy) => [enemy.id, enemy]),
  );
  const centre = (room: DungeonRoom): THREE.Vector3 =>
    new THREE.Vector3(room.coordinate.x * spacing, 0, room.coordinate.z * spacing);

  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;

  group.add(new THREE.AmbientLight(0x3a4652, 0.85));

  const connected = new Set<string>();
  for (const connection of plan.connections) {
    connected.add(`${connection.a}->${connection.b}`);
    connected.add(`${connection.b}->${connection.a}`);
  }

  const roomByCoordinate = new Map(
    plan.rooms.map((room) => [`${room.coordinate.x},${room.coordinate.z}`, room]),
  );
  const corridorsAdded = new Set<string>();

  // Structural prototypes are loaded once, measured in their authored frame, and
  // cloned into exact presentation spans. Their dimensions are never assumed.
  const floorPiece = await loadMeasured(loader, 'floor-tile', () =>
    fallbackBox(
      SCALE.maximumWallTileSpan,
      SCALE.floorThickness,
      SCALE.maximumWallTileSpan,
      0x4a4f55,
    ),
  );
  const wallPiece = await loadMeasured(loader, 'wall-straight', () =>
    fallbackBox(SCALE.maximumWallTileSpan, WALL_HEIGHT, SCALE.wallThickness, 0x565b60),
  );
  const doorwayPiece = await loadMeasured(loader, 'doorway', fallbackDoorway);
  const thresholdPiece = await loadMeasured(loader, 'threshold', () =>
    fallbackBox(SCALE.doorwayClearWidth, SCALE.thresholdHeight, SCALE.wallThickness * 2, 0x62594d),
  );

  for (const room of plan.rooms) {
    const roomCentre = centre(room);
    minX = Math.min(minX, roomCentre.x - ROOM_SIZE / 2);
    maxX = Math.max(maxX, roomCentre.x + ROOM_SIZE / 2);
    minZ = Math.min(minZ, roomCentre.z - ROOM_SIZE / 2);
    maxZ = Math.max(maxZ, roomCentre.z + ROOM_SIZE / 2);

    const floor = fittedClone(floorPiece, ROOM_SIZE, SCALE.floorThickness, ROOM_SIZE);
    floor.name = `room:${room.id}:floor`;
    floor.position.set(roomCentre.x, -SCALE.floorThickness, roomCentre.z);
    group.add(floor);

    // The source floor is turned over so its authored upper surface faces into the
    // room. Its base plane becomes the exact 5.50 m ceiling line.
    const ceiling = fittedClone(floorPiece, ROOM_SIZE, SCALE.ceilingThickness, ROOM_SIZE);
    ceiling.name = `room:${room.id}:ceiling`;
    ceiling.position.set(roomCentre.x, WALL_HEIGHT, roomCentre.z);
    ceiling.rotation.x = Math.PI;
    group.add(ceiling);

    const openSides: CardinalSide[] = [];

    for (const side of CARDINAL_SIDES) {
      const neighbour = roomByCoordinate.get(
        `${room.coordinate.x + side.dx},${room.coordinate.z + side.dz}`,
      );
      const isOpen = neighbour !== undefined && connected.has(`${room.id}->${neighbour.id}`);

      const sideAssembly = new THREE.Group();
      sideAssembly.name = `room:${room.id}:side:${side.dx},${side.dz}`;
      sideAssembly.position.set(
        roomCentre.x + side.dx * (ROOM_SIZE / 2),
        0,
        roomCentre.z + side.dz * (ROOM_SIZE / 2),
      );
      sideAssembly.rotation.y = side.yaw;

      if (isOpen && neighbour) {
        openSides.push(side);
        const sideWallSpan = (ROOM_SIZE - SCALE.doorwayFrameOuterWidth) / 2;
        tileWallSpan(
          sideAssembly,
          wallPiece,
          sideWallSpan,
          -ROOM_SIZE / 2,
          `room:${room.id}:door-left`,
        );
        tileWallSpan(
          sideAssembly,
          wallPiece,
          sideWallSpan,
          SCALE.doorwayFrameOuterWidth / 2,
          `room:${room.id}:door-right`,
        );

        const doorway = fittedClone(
          doorwayPiece,
          SCALE.doorwayFrameOuterWidth,
          WALL_HEIGHT,
          SCALE.wallThickness,
        );
        doorway.name = `room:${room.id}:doorway`;
        sideAssembly.add(doorway);

        const threshold = fittedClone(
          thresholdPiece,
          SCALE.doorwayClearWidth,
          SCALE.thresholdHeight,
          SCALE.wallThickness * 2,
        );
        threshold.name = `room:${room.id}:threshold`;
        sideAssembly.add(threshold);

        const corridorKey = [room.id, neighbour.id].sort().join('<->');
        if (!corridorsAdded.has(corridorKey)) {
          corridorsAdded.add(corridorKey);
          const corridorLength = spacing - ROOM_SIZE;
          const corridor = new THREE.Group();
          corridor.name = `corridor:${corridorKey}`;
          corridor.position.set(
            roomCentre.x + side.dx * (spacing / 2),
            0,
            roomCentre.z + side.dz * (spacing / 2),
          );
          corridor.rotation.y = side.yaw;

          const corridorFloor = fittedClone(
            floorPiece,
            SCALE.doorwayFrameOuterWidth,
            SCALE.floorThickness,
            corridorLength,
          );
          corridorFloor.name = `${corridor.name}:floor`;
          corridorFloor.position.y = -SCALE.floorThickness;
          corridor.add(corridorFloor);

          const corridorCeiling = fittedClone(
            floorPiece,
            SCALE.doorwayFrameOuterWidth,
            SCALE.ceilingThickness,
            corridorLength,
          );
          corridorCeiling.name = `${corridor.name}:ceiling`;
          corridorCeiling.position.y = WALL_HEIGHT;
          corridorCeiling.rotation.x = Math.PI;
          corridor.add(corridorCeiling);

          for (const sign of [-1, 1] as const) {
            const corridorWall = new THREE.Group();
            corridorWall.name = `${corridor.name}:wall:${sign}`;
            corridorWall.position.x =
              sign * (SCALE.doorwayClearWidth / 2 + SCALE.wallThickness / 2);
            corridorWall.rotation.y = Math.PI / 2;
            tileWallSpan(
              corridorWall,
              wallPiece,
              corridorLength,
              -corridorLength / 2,
              corridorWall.name,
            );
            corridor.add(corridorWall);
          }

          group.add(corridor);
        }
      } else if (secretArea.isEntrance(room, side)) {
        const secretSideWallSpan = (ROOM_SIZE - secretArea.entranceSpan) / 2;
        tileWallSpan(
          sideAssembly,
          wallPiece,
          secretSideWallSpan,
          -ROOM_SIZE / 2,
          `room:${room.id}:secret-left`,
        );
        tileWallSpan(
          sideAssembly,
          wallPiece,
          secretSideWallSpan,
          secretArea.entranceSpan / 2,
          `room:${room.id}:secret-right`,
        );
      } else {
        tileWallSpan(sideAssembly, wallPiece, ROOM_SIZE, -ROOM_SIZE / 2, `room:${room.id}:wall`);
      }

      group.add(sideAssembly);
    }

    const occupiedDressing: OccupiedDressingSpace[] = [];
    const template = theme.roomTemplates.find((entry) => entry.id === room.templateId);
    const templateLoader = loaderFor(template?.assetPackId ?? interiorPackId);
    const dressing = (template?.assetIds ?? []).filter(
      (assetId) =>
        !ASSEMBLY_ASSET_IDS.has(assetId) && !(room.role === 'exit' && EXIT_ASSET_IDS.has(assetId)),
    );
    const coreRadius = room.encounter ? SCALE.encounterClearRadius : SCALE.navigationCoreRadius;

    // Every authored dressing id is considered. Placement is deterministic and
    // reserves the encounter/navigation core plus a full-width route to each door.
    for (const [index, assetId] of dressing.entries()) {
      const propPiece = await loadMeasured(templateLoader, assetId, () =>
        fallbackBox(0.8, 1.1, 0.8, 0x6b5f4d),
      );
      const naturalFootprint = Math.max(propPiece.size.x, propPiece.size.z);
      let propScale = Math.min(1, SCALE.maximumDressingFootprint / naturalFootprint);
      let propRadius = Math.max(0.08, (naturalFootprint * propScale) / 2);
      let placement: THREE.Vector2 | null = null;

      // If unusually oversized authored dressing cannot fit a safe slot, reduce it
      // progressively rather than intersecting a wall, doorway lane, or encounter.
      for (let fitAttempt = 0; fitAttempt < 18 && !placement; fitAttempt += 1) {
        propRadius = Math.max(0.08, (naturalFootprint * propScale) / 2);
        placement = findDressingPlacement(
          propRadius,
          openSides,
          occupiedDressing,
          coreRadius,
          index * 11,
        );
        if (!placement) propScale *= 0.82;
      }

      // This second search only relaxes prop-to-prop spacing for pathological
      // content. Wall, doorway, and encounter exclusions remain mandatory.
      if (!placement) {
        propScale = Math.min(propScale, 0.35);
        propRadius = Math.max(0.08, (naturalFootprint * propScale) / 2);
        placement = findDressingPlacement(propRadius, openSides, [], coreRadius, index * 17);
      }
      if (!placement) continue;

      const prop = propPiece.root.clone(true);
      prop.name = `room:${room.id}:dressing:${assetId}:${index}`;
      prop.scale.setScalar(propScale);
      const isWallMounted = WALL_MOUNTED_ASSET_IDS.has(assetId);
      const mountedHeight = Math.max(
        0,
        Math.min(
          SCALE.wallMountBaseHeight,
          WALL_HEIGHT - propPiece.size.y * propScale - SCALE.ceilingThickness - 0.1,
        ),
      );
      prop.position.set(
        roomCentre.x + placement.x,
        isWallMounted ? mountedHeight : 0,
        roomCentre.z + placement.y,
      );
      prop.rotation.y = Math.atan2(-placement.x, -placement.y);
      group.add(prop);
      occupiedDressing.push({ x: placement.x, z: placement.y, radius: propRadius });
    }

    if (room.encounter) {
      const isBossRoom = room.encounter.kind === 'boss';
      if (isBossRoom) {
        const ringMaterial = new THREE.MeshBasicMaterial({
          color: 0xe8443a,
          transparent: true,
          opacity: 0.35,
          side: THREE.DoubleSide,
        });
        const ring = new THREE.Mesh(
          new THREE.RingGeometry(
            SCALE.encounterClearRadius * 0.56,
            SCALE.encounterClearRadius * 0.64,
            40,
          ),
          ringMaterial,
        );
        ring.name = `room:${room.id}:boss-ring`;
        ring.rotation.x = -Math.PI / 2;
        ring.position.set(roomCentre.x, 0.04, roomCentre.z);
        group.add(ring);
      }

      const plannedMembers = room.encounter.members.flatMap((member) =>
        Array.from({ length: member.count }, () => member),
      );
      const columns = Math.max(1, Math.ceil(Math.sqrt(plannedMembers.length)));
      const rows = Math.max(1, Math.ceil(plannedMembers.length / columns));
      const formationSpacing =
        columns <= 1
          ? 0
          : Math.min(SCALE.encounterProxySpacing, SCALE.encounterFormationSpan / (columns - 1));

      for (const [slot, member] of plannedMembers.entries()) {
        const enemy = enemyById.get(member.enemyId);
        const memberIsKnownProxy = Object.prototype.hasOwnProperty.call(
          ENEMY_PROXY_HEIGHTS,
          member.enemyId,
        );
        const proxyId =
          enemy?.proxy?.model ?? (memberIsKnownProxy ? member.enemyId : 'skirmisher-proxy');
        const proxyLoader = enemy?.proxy?.pack ? loaderFor(enemy.proxy.pack) : null;
        const proxyHeight = knownProxyHeight(proxyId);
        const proxyWidth = Math.max(0.4, proxyHeight * 0.32);
        const proxyPiece = await loadMeasured(proxyLoader, proxyId, () =>
          fallbackBox(proxyWidth, proxyHeight, proxyWidth, isBossRoom ? 0x8a3a34 : 0x777d84),
        );
        const proxy = proxyPiece.root.clone(true);
        proxy.name = `room:${room.id}:encounter:${proxyId}:${slot}`;

        const row = Math.floor(slot / columns);
        const rowStart = row * columns;
        const rowCount = Math.min(columns, plannedMembers.length - rowStart);
        const column = slot - rowStart;
        const localX = (column - (rowCount - 1) / 2) * formationSpacing;
        const localZ = (row - (rows - 1) / 2) * formationSpacing;
        proxy.position.set(roomCentre.x + localX, 0, roomCentre.z + localZ);
        proxy.rotation.y = Math.atan2(-localX, -localZ);
        group.add(proxy);
      }
    }

    if (room.role === 'exit') {
      // A portcullis remains visual exit dressing, but it is mounted against a
      // closed wall instead of occupying a connected/traversable doorway.
      const closedSide = CARDINAL_SIDES.find(
        (side) => !openSides.some((openSide) => sameSide(side, openSide)),
      );
      if (closedSide) {
        const gatePiece = await loadMeasured(templateLoader, 'portcullis', () =>
          fallbackBox(
            SCALE.doorwayFrameOuterWidth,
            SCALE.doorwayClearHeight,
            SCALE.wallThickness,
            0x3c3f45,
          ),
        );
        const gate = fittedClone(
          gatePiece,
          SCALE.doorwayFrameOuterWidth,
          SCALE.doorwayClearHeight,
          SCALE.wallThickness,
        );
        gate.name = `room:${room.id}:exit-portcullis`;
        const wallInset = SCALE.wallThickness * 0.6;
        gate.position.set(
          roomCentre.x + closedSide.dx * (ROOM_SIZE / 2 - wallInset),
          0,
          roomCentre.z + closedSide.dz * (ROOM_SIZE / 2 - wallInset),
        );
        gate.rotation.y = closedSide.yaw;
        group.add(gate);
      }

      const altarPiece = await loadMeasured(templateLoader, 'ritual-altar', () =>
        fallbackBox(1.4, 1, 1.4, 0x7a6a3f),
      );
      const altarNaturalFootprint = Math.max(altarPiece.size.x, altarPiece.size.z);
      const altarScale = Math.min(1, SCALE.maximumDressingFootprint / altarNaturalFootprint);
      const altarRadius = Math.max(0.08, (altarNaturalFootprint * altarScale) / 2);
      const altarPlacement = findDressingPlacement(
        altarRadius,
        openSides,
        occupiedDressing,
        coreRadius,
        dressing.length * 13 + 5,
      );
      if (altarPlacement) {
        const altar = altarPiece.root.clone(true);
        altar.name = `room:${room.id}:exit-altar`;
        altar.scale.setScalar(altarScale);
        altar.position.set(roomCentre.x + altarPlacement.x, 0, roomCentre.z + altarPlacement.y);
        altar.rotation.y = Math.atan2(-altarPlacement.x, -altarPlacement.y);
        group.add(altar);
      }
    }

    const torch = new THREE.PointLight(
      room.encounter?.kind === 'boss' ? 0xff8a5c : 0xffd9a0,
      room.encounter?.kind === 'boss' ? 1.1 : 0.7,
      spacing * 1.4,
      1.9,
    );
    torch.name = `room:${room.id}:light`;
    torch.position.set(roomCentre.x, WALL_HEIGHT * 0.75, roomCentre.z);
    group.add(torch);
  }

  group.add(await secretArea.build());
  minX = Math.min(minX, secretArea.bounds.minX);
  maxX = Math.max(maxX, secretArea.bounds.maxX);
  minZ = Math.min(minZ, secretArea.bounds.minZ);
  maxZ = Math.max(maxZ, secretArea.bounds.maxZ);

  const entranceRoom = plan.rooms.find((room) => room.role === 'entrance') ?? plan.rooms[0]!;
  return {
    group,
    entrance: centre(entranceRoom).add(origin),
    roomCentre: (room) => centre(room).add(origin),
    bounds: {
      minX: origin.x + minX,
      maxX: origin.x + maxX,
      minZ: origin.z + minZ,
      maxZ: origin.z + maxZ,
    },
    collision: buildInteriorCollision(plan, spacing, secretArea.collisionFacts),
  };
}
